import { useMemo } from "react";
import { useAuth } from "../contexts/AuthContext";

export function useTabPermissions() {
  const { user } = useAuth();

  const isAdmin = useMemo(() => {
    if (!user) return false;
    return user.is_master_admin || user.is_company_admin;
  }, [user]);

  const hasTabPermission = useMemo(() => {
    return (section, permission) => {
      if (!user) return false;

      // master admins and company admins have all permissions
      if (user.is_master_admin || user.is_company_admin) {
        return true;
      }

      // for non-shared sections, check role access
      if (section === "donor" && !user.can_donate) {
        return false;
      }
      if (section === "receiver" && !user.can_receive) {
        return false;
      }
      // "shared" section has no role gate — any user can access if permitted

      let tabPermissions = user.tab_permissions;

      if (!tabPermissions) {
        return false;
      }

      if (typeof tabPermissions === "string") {
        try {
          tabPermissions = JSON.parse(tabPermissions);
        } catch {
          return false;
        }
      }

      const sectionPerms = tabPermissions[section] || {};
      // for "shared" section, default to true if not explicitly set
      // (handles existing users who don't have the shared key yet)
      if (section === "shared" && !(section in tabPermissions)) {
        return true;
      }
      return sectionPerms[permission] === true;
    };
  }, [user]);

  return { hasTabPermission, isAdmin };
}

// permission key mapping for paths
export const PERMISSION_MAP = {
  // donor
  "/donor/donate-materials": { section: "donor", permission: "donate_materials" },
  "/donor/workspace": { section: "donor", permission: "donate_materials" },
  "/donor/allocate": { section: "donor", permission: "allocate" },
  "/donor/inventory": { section: "donor", permission: "inventory" },
  "/donor/ghg": { section: "donor", permission: "ghg" },
  // receiver
  "/receiver/material-management": { section: "receiver", permission: "browse_materials" },
  "/receiver/schedule": { section: "receiver", permission: "schedule" },
  "/receiver/inventory": { section: "receiver", permission: "inventory" },
  // recovery & loss reuses the inventory permission for now — same data scope.
  // if it ever needs a separate gate, add tab_permissions.receiver.flow_analysis.
  "/receiver/flow-analysis": { section: "receiver", permission: "inventory" },
  "/receiver/ghg": { section: "receiver", permission: "ghg" },
  // shared
  "/partnerships": { section: "shared", permission: "partnerships" },
};

export default useTabPermissions;
